import React, { useState, useEffect, useRef } from "react";
import { firestore } from "../../firebase/config";
import {
  collection,
  doc,
  endBefore,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  startAfter,
  where,
} from "firebase/firestore";
import styles from "./chat.module.css"; // Import your CSS module
import { AiOutlineSend } from "react-icons/ai";
import { BsCameraVideo } from "react-icons/bs";

import {
  addDocToFirestore,
  formatMessage,
  getFormattedTime,
  getGreaterID,
  getLesserID,
  makeLinksClickable,
  reactToast,
} from "../../helper";
import { toast } from "react-toastify";
import { createMeeting } from "../../services/zoom-meetings";
import { useSelector } from "react-redux";
import {
  DEMO_TYPE,
  MESSAGE_TYPES,
  MONTHLY_TYPE,
  STUDENT_ROLE,
  TEACHER_ROLE,
  offerStatuses,
} from "../../constants";
import { StudentOfferCard } from "./OfferCard";
import CreateOfferCard from "../createOfferCard/CreateOfferCard";
import { updateRequestedOfferStatus } from "../../services/offers";
import { MdOutlineKeyboardVoice } from "react-icons/md";
import { RiVoiceprintFill } from "react-icons/ri";
import { IoLocationOutline } from "react-icons/io5";
import AudioPlayer from "../audio-player";
import { UploadFile } from "../../services/uploadFiles";
import { baseUrl } from "../../services/api";

const INITIAL_OFFER_DATA = {
  offerType: "monthly",
};

const MESSAGE_LIMIT = 10;
const Chat = ({ receiver }) => {
  const currentUser = useSelector((state) => state.auth.user);

  const [init, setInit] = useState(false);
  const [messages, setMessages] = useState([]);
  const [offerDetail, setOfferDetail] = useState({
    ...INITIAL_OFFER_DATA,
  });
  const [random, setRandom] = useState([0, ""]);
  const [loading, setLoading] = useState(false);
  const [offerStatusLoading, setOfferStatusLoading] = useState(false);
  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [meetingLoading, setMeeingLoading] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [openOfferCard, setOpenOfferCard] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  useEffect(() => {
    // random[1] && getNewMessages(random[1]);
  }, [random]);

  const getNewMessages = async (chatDocRef) => {
    try {
      let lastMessage = messages[0]?.timestamp || null;

      const nextQuery = query(
        collection(chatDocRef, "messages"),
        orderBy("timestamp", "desc"),
        lastMessage ? endBefore(lastMessage) : limit(MESSAGE_LIMIT) // Start after the last message's timestamp or null
      );

      const querySnapshot = await getDocs(nextQuery);

      const newMessages = await querySnapshot.docs.map((doc) => doc.data());

      setMessages([...newMessages, ...messages]);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    setInitialLoading(true);
    setMessages([]);

    const chatID = `${getLesserID(
      currentUser?.uuid,
      receiver?.uuid
    )}_${getGreaterID(currentUser?.uuid, receiver?.uuid)}`;

    const chatRef = doc(firestore, `chats/${chatID}`);
    const messagesQuery = query(
      collection(chatRef, "messages"),
      orderBy("timestamp", "desc"),
      limit(MESSAGE_LIMIT)
    );

    const unsub = onSnapshot(messagesQuery, (messagesSnapshot) => {
      const updatedMessages = messagesSnapshot.docs.map((doc) => ({
        chatId: chatRef.id,
        messageId: doc.id,
        ...doc.data(),
      }));
      setMessages(updatedMessages);
      setInitialLoading(false);
    });

    return unsub;
  }, [receiver?.uuid, currentUser?.uuid]);

  useEffect(() => {
    if (!init && messages?.length) {
      scrollToBottom();
      setInit(true);
    }
  }, [init, messages]);

  useEffect(() => {
    inputRef.current.focus();
  }, []);

  // Define a function to load more messages
  // new functionality to load more messages
  const loadMoreMessages = async (lmt, messages) => {
    try {
      setLoadingMoreMessages(true);

      const chatID = `${getLesserID(
        currentUser?.uuid,
        receiver?.uuid
      )}_${getGreaterID(currentUser?.uuid, receiver?.uuid)}`;

      const chatRef = doc(firestore, `chats/${chatID}`);
      const messagesCollectionRef = collection(chatRef, "messages");
      // Get the last visible message
      const lastVisibleMessage =
        messages[messages.length - 1]?.timestamp || null;
      // Query for the next batch of messages starting after the last visible message
      const nextQuery = query(
        messagesCollectionRef,
        orderBy("timestamp", "desc"),
        startAfter(lastVisibleMessage), // Start after the last message's timestamp or null
        limit(lmt)
      );
      // Get the next batch of messages
      const querySnapshot = await getDocs(nextQuery);
      // Convert the query snapshot into an array of message data
      const newMessages = querySnapshot.docs.map((doc) => doc.data());
      // Add the new messages to the existing messages array
      setMessages((prevMessages) => [...prevMessages, ...newMessages]);

      setLoadingMoreMessages(false);
    } catch (error) {
      setLoadingMoreMessages(false);
      console.error("Error loading more messages:", error);
    }
  };

  // new Funcitionality to send message
  const sendMessage = async (content, offerData = null, msgType) => {
    try {
      if (!content || content?.trim() === "") return;
      setLoading(true);
      setNewMessage("");
      const userID = currentUser?.uuid;
      const receiverID = receiver?.uuid;

      const commonMessageData = {
        sender_uuid: userID,
        senderName: currentUser?.fullName,
        receiver_uuid: receiverID,
        receiverId: receiver?.id,
        receiverName: receiver?.fullName,
        receiverProfileUrl:
          receiver?.teacherMedia?.find((x) => x?.type === "profilePicture")
            ?.url || "",
        timestamp: new Date(),
        seen: false,
      };
      let messageBody = {};

      // is offer type
      if (openOfferCard) {
        messageBody = {
          text: content,
          message_type: msgType || MESSAGE_TYPES.OFFER,
          offerDetail: offerData,
          ...commonMessageData,
        };
      }
      if (msgType === MESSAGE_TYPES.TEXT) {
        messageBody = {
          text: content,
          message_type: msgType,
          ...commonMessageData,
        };
      } else if (msgType === MESSAGE_TYPES.AUDIO) {
        messageBody = {
          audioUrl: content,
          message_type: msgType,
          ...commonMessageData,
        };
      }
      setMessages([messageBody, ...messages]);
      scrollToBottom();
      await addDocToFirestore(currentUser?.uuid, receiver?.uuid, messageBody);
      setLoading(false);
    } catch (error) {
      toast.error("Message is not sent, try again");
      console.log("Error sending message:", error);
      setLoading(false);
    }
  };

  // offer status update functionality
  const updateOfferStatus = async (
    chatId,
    messageId,
    newOfferStatus,
    message
  ) => {
    try {
      setOfferStatusLoading(true);
      const chatRef = firestore.collection("chats").doc(chatId);
      const messageRef = chatRef.collection("messages").doc(messageId);

      let updatedOfferDetail = {
        ...message,
        offerDetail: {
          ...message.offerDetail,
          offerStatus: newOfferStatus,
        },
      };

      let payload = {
        status: newOfferStatus,
      };
      const { status, data } = await updateRequestedOfferStatus(
        message?.offerDetail?.offerId,
        payload
      );
      if (status === 200) {
        await messageRef.update(updatedOfferDetail);
        // await messageRef.update({ "offerDetail.offerStatus": newOfferStatus });

        reactToast("success", data?.message);
      } else {
        reactToast("error", data?.message);
      }
      setOfferStatusLoading(false);
    } catch (error) {
      setOfferStatusLoading(false);

      console.error("Error updating offer status:", error);
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current.stop();
    setIsRecording(false);
  };

  const startRecording = React.useCallback(() => {
    audioChunksRef.current = [];
    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        mediaRecorderRef.current = new MediaRecorder(stream);
        mediaRecorderRef.current.ondataavailable = (event) => {
          audioChunksRef.current.push(event.data);
        };

        mediaRecorderRef.current.onstop = async () => {
          const audioBlob = new Blob(audioChunksRef.current, {
            type: "audio/wav",
          });
          // const audioUrl = URL.createObjectURL(audioBlob);
          console.log("audioBlob", audioBlob);
          const formData = new FormData();
          formData.append("file", audioBlob, "recording.wav");

          let { data } = await UploadFile(formData);
          await sendMessage(data?.data?.url, null, MESSAGE_TYPES.AUDIO);
        };
        mediaRecorderRef.current.start();
        setIsRecording(true);
      })
      .catch((err) => {
        console.error("Error accessing microphone: ", err);
      });
    // eslint-disable-next-line
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
  };

  const createZoomMeeting = async () => {
    try {
      setMeeingLoading(true);
      const payload = {
        offerSlotId: null,
      };
      let { status, data } = await createMeeting(payload);
      if (status === 201) {
        const userID = currentUser?.uuid;
        const receiverID = receiver?.uuid;
        const messageBody = {
          text: data?.data?.join_url, // for student
          zoom_host_url: data?.data?.start_url, // for teacher
          zoom_guest_url: data?.data?.join_url, // for student
          sender_uuid: userID,
          senderName: currentUser?.fullName,
          receiverId: receiver?.id,
          receiver_uuid: receiverID,
          receiverName: receiver?.fullName,
          timestamp: new Date(),
          seen: false,
        };
        await addDocToFirestore(currentUser?.uuid, receiver?.uuid, messageBody);
        scrollToBottom();
      } else {
        reactToast("error", "Something went wrong!");
      }
      setMeeingLoading(false);
    } catch (error) {
      toast.error("Something went wrong!");
      setMeeingLoading(false);
    }
  };

  const handleCreateOfferCardModal = (offerType) => {
    setOpenOfferCard(!openOfferCard);
    if (offerType) {
      setOfferDetail({ ...INITIAL_OFFER_DATA, offerType });
    } else {
      setOfferDetail({ ...INITIAL_OFFER_DATA });
    }
  };

  const sendOffer = async (offerData) => {
    try {
      await sendMessage("New Offer Request", {
        ...offerData,
        offerStatus: offerStatuses.PENDING,
      });
      if (openOfferCard) {
        handleCreateOfferCardModal(MONTHLY_TYPE);
      }
    } catch (error) {}
  };

  const checkZoomUrl = (message) => {
    let hasZoomGuestUrl;

    if (currentUser?.role === TEACHER_ROLE) {
      if (message?.zoom_host_url) {
        hasZoomGuestUrl = message.zoom_host_url;
      } else {
        hasZoomGuestUrl = message.text;
      }
    } else if (currentUser?.role === STUDENT_ROLE) {
      if (message?.zoom_guest_url) {
        hasZoomGuestUrl = message.zoom_guest_url;
      } else {
        hasZoomGuestUrl = message.text;
      }
    } else {
      hasZoomGuestUrl = message.text;
    }
    return hasZoomGuestUrl;
  };
  return (
    <>
      {/* offer card modal */}
      <CreateOfferCard
        setOfferDetail={setOfferDetail}
        offerDetail={offerDetail}
        // when offer will send by student
        teacherId={
          currentUser?.role === TEACHER_ROLE ? currentUser?.id : receiver.id
        }
        studentId={
          currentUser?.role === TEACHER_ROLE ? receiver.id : currentUser?.id
        }
        onSendOffer={sendOffer}
        handleOk={handleCreateOfferCardModal}
        handleCancel={handleCreateOfferCardModal}
        open={openOfferCard}
      />

      <div className="mt-4 p-4 primary_bg_color rounded-4">
        <div className=" mb-2">
          <span className="fs_12 text-light ">
            Chat with{" "}
            <i>
              <b>{receiver?.fullName}</b>
            </i>
          </span>
        </div>
        <div className="fs_12 text-light">
          <b>Disclaimer: </b>
          For your privacy and security, please do not share any personal
          information such as phone number, email address.
        </div>
        {receiver?.message ? (
          <p className="fs_12 text-light">
            Subject <b>{receiver?.message}</b>
          </p>
        ) : (
          ""
        )}

        <div
          style={{ height: "300px", overflowY: "scroll" }}
          className="message_area"
        >
          {loadingMoreMessages ? (
            <p className="mt-2 fs_12 text-light d-flex justify-content-center cursor_pointer">
              Loading...
            </p>
          ) : messages.length >= 10 ? (
            <p
              className="mt-2 fs_12 text-light d-flex justify-content-center cursor_pointer"
              onClick={() => loadMoreMessages(MESSAGE_LIMIT, messages)}
            >
              Load previous messages
            </p>
          ) : (
            ""
          )}
          {messages?.length ? (
            messages
              .slice()
              .reverse()
              ?.map((message, index) => {
                let hasZoomGuestUrl = checkZoomUrl(message);

                const formattedText = makeLinksClickable(
                  hasZoomGuestUrl,
                  message,
                  currentUser?.role
                );

                // own messages
                if (message.sender_uuid === currentUser?.uuid) {
                  return (
                    <div key={index} className="d-flex justify-content-end ">
                      <div
                        // style={{ width: "55%" }}
                        className={`primary_dark_bg_color p-2 ${styles["message_sender"]} ${styles["custom_width"]}`}
                      >
                        {message?.message_type === MESSAGE_TYPES.AUDIO ? (
                          <AudioPlayer
                            src={`${baseUrl}/${message?.audioUrl}`}
                          />
                        ) : (
                          <div
                            className="text-white"
                            dangerouslySetInnerHTML={{
                              __html: formatMessage(formattedText, 5),
                            }}
                          />
                        )}

                        {message?.offerDetail && (
                          <StudentOfferCard
                            loading={offerStatusLoading}
                            currentUser={currentUser}
                            message={message}
                            updateOfferStatus={updateOfferStatus}
                          />
                        )}
                        <div className={styles["message-time"]}>
                          {getFormattedTime(message?.timestamp)}
                        </div>
                      </div>
                    </div>
                  );
                }
                // upcoming messages
                else {
                  return (
                    <div
                      key={index}
                      className={` ${styles["message"]} ${styles["received"]}`}
                    >
                      {message?.message_type === MESSAGE_TYPES.AUDIO ? (
                        <AudioPlayer src={`${baseUrl}/${message?.audioUrl}`} />
                      ) : (
                        <div
                          className={styles["message-text"]}
                          dangerouslySetInnerHTML={{
                            __html: formatMessage(formattedText, 5),
                          }}
                        />
                      )}

                      {message?.offerDetail && (
                        <StudentOfferCard
                          loading={offerStatusLoading}
                          currentUser={currentUser}
                          message={message}
                          updateOfferStatus={updateOfferStatus}
                        />
                      )}
                      <div className={styles["message-time"]}>
                        {getFormattedTime(message?.timestamp)}
                      </div>
                    </div>
                  );
                }
              })
          ) : initialLoading ? (
            <p
              style={{ height: "256px" }}
              className="fs_12 text-light d-flex justify-content-center align-items-center"
            >
              Loading messages...
            </p>
          ) : (
            <p
              style={{ height: "256px" }}
              className="fs_12 text-light d-flex justify-content-center align-items-center"
            >
              No message to display.
            </p>
          )}
          <div ref={messagesEndRef} />
        </div>

        {loading ? (
          <p className="fs_12 text-light d-flex justify-content-center align-items-center">
            {/* updating messages... */}
          </p>
        ) : (
          ""
        )}
        <div className="mt-5 d-lg-flex">
          <div
            style={{ height: "44px", width: "87%" }}
            className=" rounded-4 bg-white px-3 py-2 d-flex justify-content-between"
          >
            <input
              ref={inputRef}
              onKeyUp={(e) => {
                if (e.key === "Enter" && !loading && !initialLoading) {
                  sendMessage(newMessage, null, MESSAGE_TYPES.TEXT);
                }
              }}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Write a message..."
              className="form-control border-0 fs_12 form_control_focus_box_shadow_none"
              aria-label="With textarea"
            />
            {loading || initialLoading ? (
              <AiOutlineSend
                style={{
                  position: "relative",
                  top: "5px",
                  left: "7px",
                  cursor: "pointer",
                }}
                color="blue"
                size={20}
              />
            ) : (
              <AiOutlineSend
                onClick={() =>
                  sendMessage(newMessage, null, MESSAGE_TYPES.TEXT)
                }
                style={{
                  position: "relative",
                  top: "5px",
                  left: "7px",
                  cursor: "pointer",
                }}
                color="blue"
                size={20}
              />
            )}
          </div>
          <div style={{ paddingTop: "10px" }} className=" pe-2 ps-2">
            <div className="d-flex justify-content-space-between gap-1">
              {currentUser?.role === TEACHER_ROLE && (
                <>
                  {meetingLoading ? (
                    <div className="spinner-border text-light" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  ) : (
                    <BsCameraVideo
                      title="Create zoom meeting"
                      className="cursor_pointer"
                      onClick={() => createZoomMeeting()}
                      size={25}
                      color="white"
                    />
                  )}
                </>
              )}
              {isRecording ? (
                <RiVoiceprintFill
                  title="Voice listening"
                  className={`cursor_pointer ${
                    isRecording && styles["recording-icon"]
                  }`}
                  onClick={() => stopRecording()}
                  size={25}
                  color="white"
                />
              ) : (
                <MdOutlineKeyboardVoice
                  title="Create voice recording"
                  className="cursor_pointer"
                  onClick={() => startRecording()}
                  size={25}
                  color="white"
                />
              )}
              {/* <IoLocationOutline
                title="Share Location"
                size={25}
                className="cursor"
                color="white"
              /> */}
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-center">
          {currentUser?.role === STUDENT_ROLE && (
            <button
              onClick={() => handleCreateOfferCardModal(MONTHLY_TYPE)}
              type="button"
              className="btn btn-primary btn-sm mt-2 me-3"
            >
              Create Offer
            </button>
          )}

          <button
            onClick={() => handleCreateOfferCardModal(DEMO_TYPE)}
            type="button"
            className="btn btn-primary btn-sm mt-2"
          >
            Schedule Demo
          </button>
        </div>
      </div>
    </>
  );
};

export default Chat;
